import { ChangeDetectorRef, Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/services/config.service';
import { ClaudeAuthWizardService, AuthStatus } from 'src/app/services/claude-auth-wizard.service';
import { firstValueFrom, Subscription } from 'rxjs';

export interface BackendInfo {
  id: string;
  name: string;
  display_name: string;
  description: string;
  enabled: boolean;
  healthy: boolean;
  configured: boolean;
  config_type: 'api_key' | 'cli_auth' | 'local' | 'none';
  config_instructions?: string;
  config_url?: string;
  install_command?: string;
  models: string[];
}

export interface TokenDetails {
  scopes?: string[];
  expires_at?: number;
  expires_in?: string;
}

export interface BackendStatus {
  id: string;
  registered: boolean;
  enabled: boolean;
  healthy: boolean;
  configured: boolean;
  auth_status: 'installed' | 'authenticated' | 'not_installed' | 'not_authenticated' | 'token_expired' | 'error' | null;
  error: string | null;
  warning?: string | null;
  token_details?: TokenDetails;
}

type SetupStep = 'list' | 'configure';

@Component({
    selector: 'app-backend-manager-widget',
    templateUrl: './backend-manager-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './backend-manager-widget.component.scss'],
    standalone: false
})
export class BackendManagerWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  @Output() onOpenTerminal = new EventEmitter<void>();

  public backends: BackendInfo[] = [];
  public isLoading = true;
  public errorMessage: string | null = null;

  // Current view
  public currentStep: SetupStep = 'list';
  public selectedBackend: BackendInfo | null = null;
  public backendStatus: BackendStatus | null = null;

  // Configuration form
  public apiKey = '';
  public hostUrl = '';
  public selectedModel = '';  // Selected model for this backend
  public isSaving = false;
  public isCheckingStatus = false;

  // Claude CLI auth wizard state
  private _isAuthenticating = false;
  public get isAuthenticating(): boolean {
    return this._isAuthenticating;
  }
  public set isAuthenticating(value: boolean) {
    console.log('isAuthenticating SET TO:', value, new Error().stack);
    this._isAuthenticating = value;
  }
  public authWizardStatus: AuthStatus | null = null;
  public authWizardError: string | null = null;
  public authCode = '';  // OAuth code input from user
  public isSubmittingCode = false;
  private authStatusSub: Subscription | null = null;
  private authErrorSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private authWizard: ClaudeAuthWizardService,
    private _cdr: ChangeDetectorRef
  ) {
    super();
    (window as any).authComp = this;
    console.log('BackendManagerWidget assigned to window.authComp');
  }

  async ngOnInit(): Promise<void> {
    await this.loadBackends();

    // Subscribe to auth wizard status updates
    // NOTE: We intentionally do NOT set isAuthenticating = false here based on status.
    // The user's intent to re-authenticate must be preserved regardless of status calls.
    // Only explicit user actions (cancel, successful code submit) should end the auth flow.
    this.authStatusSub = this.authWizard.authStatus$.subscribe(status => {
      this.authWizardStatus = status;
      this._cdr.detectChanges();
    });

    this.authErrorSub = this.authWizard.error$.subscribe(error => {
      this.authWizardError = error;
      this._cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    // Cleanup auth wizard subscriptions
    this.authStatusSub?.unsubscribe();
    this.authErrorSub?.unsubscribe();
    this.authWizard.reset();
  }

  async loadBackends(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const response = await firstValueFrom(
        this.http.get<BackendInfo[]>(`${ConfigService.muxUrl}/backends/info`)
      );
      const allowed = ConfigService.allowedBackends;
      this.backends = allowed ? response.filter(b => allowed.includes(b.id)) : response;
    } catch (error) {
      console.error('Failed to load backends:', error);
      this.errorMessage = 'Failed to load backends';
    } finally {
      this.isLoading = false;
      // Trigger change detection for dynamic component in OnPush parent
      this._cdr.detectChanges();
    }
  }

  async selectBackend(backend: BackendInfo): Promise<void> {
    this.selectedBackend = backend;
    this.currentStep = 'configure';
    this.apiKey = '';
    this.hostUrl = '';
    // Default to first model if available
    this.selectedModel = backend.models.length > 0 ? backend.models[0] : '';
    await this.checkBackendStatus();
  }

  async checkBackendStatus(): Promise<void> {
    if (!this.selectedBackend) return;

    this.isCheckingStatus = true;
    try {
      const response = await firstValueFrom(
        this.http.get<BackendStatus>(`${ConfigService.muxUrl}/backends/${this.selectedBackend.id}/status`)
      );
      this.backendStatus = response;
    } catch (error) {
      console.error('Failed to check backend status:', error);
    } finally {
      this.isCheckingStatus = false;
    }
  }

  backToList(): void {
    // Update the cached backend with current status before going back
    if (this.selectedBackend && this.backendStatus) {
      const idx = this.backends.findIndex(b => b.id === this.selectedBackend!.id);
      if (idx !== -1) {
        this.backends[idx] = {
          ...this.backends[idx],
          healthy: this.backendStatus.healthy,
          configured: this.backendStatus.configured,
        };
      }
    }

    this.currentStep = 'list';
    this.selectedBackend = null;
    this.backendStatus = null;

    // Refresh in background without blocking UI
    this.refreshBackendsInBackground();
  }

  private async refreshBackendsInBackground(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<BackendInfo[]>(`${ConfigService.muxUrl}/backends/info`)
      );
      const allowed = ConfigService.allowedBackends;
      this.backends = allowed ? response.filter(b => allowed.includes(b.id)) : response;
    } catch (error) {
      // Silently fail - we already have cached data
      console.debug('Background refresh failed:', error);
    }
  }

  async saveApiKeyConfig(): Promise<void> {
    if (!this.selectedBackend || !this.apiKey.trim()) return;

    this.isSaving = true;
    this.errorMessage = null;

    try {
      const config: any = { api_key: this.apiKey.trim() };
      // Include selected model if one was chosen
      if (this.selectedModel) {
        config.model = this.selectedModel;
      }
      await firstValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/${this.selectedBackend.id}/configure`, config)
      );
      await this.checkBackendStatus();
      // Show success - stay on page so user can verify
    } catch (error: any) {
      console.error('Failed to configure backend:', error);
      this.errorMessage = error?.error?.detail || 'Failed to save configuration';
    } finally {
      this.isSaving = false;
    }
  }

  async saveHostConfig(): Promise<void> {
    if (!this.selectedBackend || !this.hostUrl.trim()) return;

    this.isSaving = true;
    this.errorMessage = null;

    try {
      await firstValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/${this.selectedBackend.id}/configure`, {
          host: this.hostUrl.trim()
        })
      );
      await this.checkBackendStatus();
    } catch (error: any) {
      console.error('Failed to configure backend:', error);
      this.errorMessage = error?.error?.detail || 'Failed to save configuration';
    } finally {
      this.isSaving = false;
    }
  }

  openTerminal(): void {
    this.onOpenTerminal.emit();
  }

  /**
   * Start Claude CLI OAuth authentication.
   * This captures the OAuth URL and opens it in a new tab.
   */
  async startClaudeAuth(): Promise<void> {
    this.isAuthenticating = true;
    this.authWizardError = null;
    this._cdr.detectChanges();

    try {
      // If already authenticated, logout first to force re-authentication
      if (this.backendStatus?.auth_status === 'authenticated') {
        try {
          await firstValueFrom(
            this.http.post(`${ConfigService.muxUrl}/backends/claude-cli/auth/logout`, {})
          );
        } catch (e) {
          // Ignore logout errors - proceed with auth anyway
          console.debug('Logout before re-auth failed (may be ok):', e);
        }
      }

      const session = await this.authWizard.startAuth();

      if (session.oauth_url) {
        // Open OAuth URL in new tab
        window.open(session.oauth_url, '_blank');
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
      // Note: even if session.status === 'authenticated', we keep isAuthenticating = true
      // so the user can complete re-authentication. Only errors or explicit cancel should hide the form.
      this._cdr.detectChanges();
    } catch (error: any) {
      console.error('Failed to start Claude auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
      this._cdr.detectChanges();
    }
  }

  /**
   * Cancel in-progress Claude authentication.
   */
  async cancelClaudeAuth(): Promise<void> {
    await this.authWizard.cancelAuth();
    this.isAuthenticating = false;
    this.authCode = '';
  }

  /**
   * Save model selection (for backends that don't need API key, like Claude CLI).
   */
  async saveModelConfig(): Promise<void> {
    if (!this.selectedBackend || !this.selectedModel) return;

    this.isSaving = true;
    this.errorMessage = null;

    try {
      await firstValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/${this.selectedBackend.id}/configure`, {
          model: this.selectedModel
        })
      );
      await this.checkBackendStatus();
    } catch (error: any) {
      console.error('Failed to save model config:', error);
      this.errorMessage = error?.error?.detail || 'Failed to save model configuration';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Submit the OAuth code to complete authentication.
   */
  async submitAuthCode(): Promise<void> {
    if (!this.authCode.trim()) {
      return;
    }

    this.isSubmittingCode = true;
    this.authWizardError = null;

    try {
      const result = await this.authWizard.submitCode(this.authCode.trim());

      if (result.success) {
        // Code submitted - polling will detect completion
        // If already authenticated, refresh status
        if (this.authWizardStatus === 'authenticated') {
          this.isAuthenticating = false;
          this.authCode = '';
          await this.checkBackendStatus();
        }
      } else {
        this.authWizardError = result.message || 'Failed to submit code';
      }
    } catch (error: any) {
      console.error('Failed to submit auth code:', error);
      this.authWizardError = error?.message || 'Failed to submit code';
    } finally {
      this.isSubmittingCode = false;
    }
  }

  /**
   * Get human-readable auth wizard status text.
   */
  getAuthWizardStatusText(): string {
    switch (this.authWizardStatus) {
      case 'starting':
        return 'Starting authentication...';
      case 'waiting':
        return 'Complete authentication in your browser...';
      case 'authenticated':
        return 'Authenticated successfully!';
      case 'failed':
        return this.authWizardError || 'Authentication failed';
      case 'cancelled':
        return 'Authentication cancelled';
      default:
        return '';
    }
  }

  getStatusBadgeClass(backend: BackendInfo): string {
    return backend.healthy ? 'status-ready' : 'status-not-configured';
  }

  getStatusText(backend: BackendInfo): string {
    return backend.healthy ? 'Ready' : 'Not Configured';
  }

  getAuthStatusText(): string {
    if (!this.backendStatus) return 'Unknown';
    switch (this.backendStatus.auth_status) {
      case 'authenticated':
        if (this.backendStatus.warning) {
          return `⚠️ ${this.backendStatus.warning}`;
        }
        return '✅ Authenticated';
      case 'token_expired': return `❌ ${this.backendStatus.error || 'Token expired'}`;
      case 'installed': return '⚠️ Installed but not authenticated';
      case 'not_installed': return '❌ Not installed';
      case 'not_authenticated': return '⚠️ Not authenticated';
      case 'error': return `❌ Error: ${this.backendStatus.error}`;
      default: return 'Unknown';
    }
  }

  /**
   * Check if we should show the authenticate button.
   * Always show it unless actively authenticating - better UX to allow re-auth anytime.
   */
  shouldShowAuthButton(): boolean {
    if (this.isAuthenticating) return false;
    if (this.isCheckingStatus && !this.backendStatus) return false;
    return true;
  }

  /**
   * Get appropriate label for the auth button based on current status.
   */
  getAuthButtonLabel(): string {
    if (!this.backendStatus) return 'Authenticate with Claude';
    switch (this.backendStatus.auth_status) {
      case 'authenticated': return 'Re-authenticate';
      case 'token_expired': return 'Refresh Authentication';
      default: return 'Authenticate with Claude';
    }
  }

  openConfigUrl(): void {
    if (this.selectedBackend?.config_url) {
      window.open(this.selectedBackend.config_url, '_blank');
    }
  }
}
